/**
 * I18nFormFields 컴포넌트
 * 4개 언어 입력 필드를 한 화면에 표시하는 다국어 입력 컴포넌트
 * Ant Design Form과 통합되어 실시간 검증 및 오류 메시지 표시 기능 제공
 */

import { Form, Input, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getEnabledLanguages,
  getRequiredLanguages,
  NON_ENGLISH_LANGUAGES,
  SUPPORTED_LANGUAGES,
  type LanguageCode,
} from '../../constants/i18n/languages';
import { I18nValidation } from '../../services/i18n/i18nValidation';
import {
  type I18nFieldData,
  type I18nFieldName,
  type I18nFormFieldsProps,
} from '../../types/i18n/i18nTypes';
import './I18nFormFields.css';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * I18nFormFields 컴포넌트
 *
 * @param fieldName - 필드 이름 (title, synopsis)
 * @param required - 필수 필드 여부
 * @param fieldType - 입력 타입 (input, textarea)
 * @param value - 현재 i18n 값
 * @param onChange - 값 변경 핸들러
 * @param enabledLanguages - 활성화된 언어 목록 (기본값: 모든 활성화된 언어)
 * @param disabled - 비활성화 여부
 * @param placeholder - 언어별 플레이스홀더
 * @param validateTrigger - 검증 트리거 ('onChange' | 'onBlur' | 'onSubmit')
 * @param showValidationSummary - 검증 요약 표시 여부
 */
export const I18nFormFields: React.FC<I18nFormFieldsProps> = ({
  fieldName,
  required = false,
  fieldType = 'input',
  value = {},
  onChange,
  enabledLanguages,
  requiredLanguages: propRequiredLanguages,
  disabled = false,
  placeholder = {},
  validateTrigger = 'onChange',
}) => {
  // 활성화된 언어 목록 계산
  const activeLanguages = useMemo(() => {
    return enabledLanguages || getEnabledLanguages();
  }, [enabledLanguages]);

  // 필수 언어 목록 계산
  const requiredLanguages = useMemo(() => {
    return propRequiredLanguages || getRequiredLanguages();
  }, [propRequiredLanguages]);

  // 실시간 검증 상태
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<LanguageCode, string>>>({});
  const [_globalError, setGlobalError] = useState<string>('');
  const [, setHasNonEnglishContent] = useState<boolean>(false);

  // 비영어 언어 콘텐츠 존재 여부 확인
  useEffect(() => {
    const hasContent = NON_ENGLISH_LANGUAGES.some(language => value[language]?.trim());
    setHasNonEnglishContent(hasContent);

    // 전역 검증 (비영어 언어 필수 검증)
    if (required && !hasContent) {
      setGlobalError(
        '영어가 아닌 언어 중 최소 1개는 필수입니다 (한국어, 중국어 간체, 중국어 번체)'
      );
    } else {
      setGlobalError('');
    }
  }, [value, required]);

  // 개별 언어 필드 값 변경 핸들러
  const handleFieldChange = useCallback(
    (language: LanguageCode, fieldValue: string) => {
      if (!onChange) return;

      const newValue: I18nFieldData = {
        ...value,
        [language]: fieldValue, // 빈 문자열도 그대로 유지하여 API에서 명시적으로 빈값 전송
      };

      // 실시간 검증 (onChange 트리거인 경우)
      if (validateTrigger === 'onChange') {
        const validation = I18nValidation.validateRealtime(
          fieldName as I18nFieldName,
          language,
          fieldValue
        );

        setFieldErrors(prev => ({
          ...prev,
          [language]: validation.isValid ? '' : validation.message || '',
        }));
      }

      onChange(newValue);
    },
    [value, onChange, fieldName, validateTrigger]
  );

  // 필드 블러 핸들러 (onBlur 검증)
  const handleFieldBlur = useCallback(
    (language: LanguageCode, fieldValue: string) => {
      if (validateTrigger === 'onBlur') {
        const validation = I18nValidation.validateRealtime(
          fieldName as I18nFieldName,
          language,
          fieldValue
        );

        setFieldErrors(prev => ({
          ...prev,
          [language]: validation.isValid ? '' : validation.message || '',
        }));
      }
    },
    [fieldName, validateTrigger]
  );

  // 언어별 필드 렌더링
  const renderLanguageField = useCallback(
    (language: LanguageCode) => {
      const languageInfo = SUPPORTED_LANGUAGES[language];
      const isRequired = required && requiredLanguages.includes(language);
      const fieldValue = value[language] || '';
      const fieldPlaceholder = placeholder[language] || `${languageInfo.label} Enter ${fieldName}`;
      const hasError = fieldErrors[language];

      const InputComponent = fieldType === 'textarea' ? TextArea : Input;
      const inputProps =
        fieldType === 'textarea' ? { rows: 3, autoSize: { minRows: 3, maxRows: 6 } } : {};
      const inputStyles =
        fieldType === 'textarea'
          ? { textarea: { color: 'rgba(0, 0, 0, 0.85)' } }
          : { input: { color: 'rgba(0, 0, 0, 0.85)' } };

      return (
        <Form.Item
          key={language}
          className="i18n-field-item"
          validateStatus={hasError ? 'error' : ''}
          help={hasError}
        >
          <div className="i18n-field-label">
            <Text style={{ marginBottom: '10px' }} strong className="i18n-language-name">
              {languageInfo.label}
            </Text>
            {isRequired && <span className="i18n-required-indicator">*</span>}
          </div>
          <InputComponent
            value={fieldValue}
            placeholder={fieldPlaceholder}
            disabled={disabled}
            onChange={e => handleFieldChange(language, e.target.value)}
            onBlur={e => handleFieldBlur(language, e.target.value)}
            className={`i18n-input ${isRequired ? 'i18n-required' : ''} ${hasError ? 'i18n-error' : ''}`}
            status={hasError ? 'error' : undefined}
            style={{ color: 'rgba(0, 0, 0, 0.85)' }}
            styles={inputStyles}
            {...inputProps}
          />
        </Form.Item>
      );
    },
    [
      fieldName,
      fieldType,
      value,
      placeholder,
      disabled,
      required,
      requiredLanguages,
      fieldErrors,
      handleFieldChange,
      handleFieldBlur,
    ]
  );

  const fieldTitle = useMemo(() => {
    const fieldNames: Record<string, string> = {
      title: 'Title',
      synopsis: 'Synopsis',
      secondary_title: 'Secondary(alt language) title',
      seo_title: 'SEO Title',
      seo_synopsis: 'SEO Synopsis',
      seo_keyword: 'SEO Keyword',
      name: 'Name',
      description: 'Description',
    };
    return fieldNames[fieldName] || fieldName;
  }, [fieldName]);

  return (
    <div className="i18n-form-fields">
      <div className="i18n-fields-header">
        <Text strong className="i18n-field-title">
          {fieldTitle}
        </Text>
        {required && (
          <Text type="secondary" className="i18n-requirement-note">
            * required language must be filled in at least one
          </Text>
        )}
      </div>

      {/* 전역 검증 오류 표시 */}
      {/* {showValidationSummary && globalError && (
        <Alert
          message="필수 입력 오류"
          description={globalError}
          type="error"
          showIcon
          className="i18n-global-error"
          style={{ marginBottom: 16 }}
        />
      )} */}
      <div className="i18n-fields-container">{activeLanguages.map(renderLanguageField)}</div>
    </div>
  );
};

export default I18nFormFields;
